import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useAxiosInstance } from "../../services/Interceptor";
import {
  adminLikeAthlete,
  getCoachesThatLikeAthlete,
  likeAthlete,
  updateAdminLike,
} from "../../services/AthleteService";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import AthleteSearchEvent from "./athlete-search-event";
import LoadingSpinner from "../LoadingSpinner";
import CircularProgressBar from "../CircularProgressBar";
import { usePremiumUpsellRedirect } from "../../hooks/usePremiumUpsellRedirect";
import { useDispatch } from "react-redux";
import CopyUrl from "./share-profile/copy-url";
import ShareProfileCardButton from "./share-profile/share-profile-card-button";
import ShareProfileCardUpsellButton from "./share-profile/share-profile-card-upsell-button";
import AthleteCompareModal from "../../components/AthleteCompareModal";
import CompareModalTrigger from "../athlete-compare/CompareModalTrigger";
import PremiumFeatureModal from "../PremiumFeatureModal";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faThumbsUp } from "@fortawesome/free-regular-svg-icons";
import { setProfileAction } from "../../store/AthleteActions";
import FreeTrialModal from "../FreeTrialModal";
import { StartFreeTrial } from "../../services/PaymentService";
import {
  adjustAdminLikeCountLocal,
  bumpMenuRefresh,
  toggleFavouriteLocal,
  setProfileLikesForAthlete,
} from "../../store/slices/athleteSlice";
import { validateSubscriptionAction } from "../../store/userActions";
import { getStateAbbreviation } from "../../utils/stateAbbreviations";
import CoachesWhoLikedDialog from "./CoachesWhoLikedDialog";
import AthleteMeasurementVideoViewer from "./measurements/athlete_measurement_videoviewer";

function AthleteInformation({
  sharedProfile = false,
  showCompare = true,
  profile = {},
  onFavoritesRefresh,
}) {
  const authUser = useAuthUser();
  const { user = {} } = authUser;
  const apiInstance = useAxiosInstance();
  const r_athlete = useSelector((state) => state.athletes);
  const hasSubscription = useSelector((state) => state.user.hasSubscription);
  const subscriptionChecked = useSelector(
    (state) => state.user.subscriptionChecked,
  );
  const HighSchoolCoach = user?.role?.toLowerCase?.() === "highschool";
  const isAdmin = user?.role === "admin";
  const hasSubscriptionAccess = hasSubscription || HighSchoolCoach;
  let [isOpen, setIsOpen] = useState(false);
  const [coaches, setCoaches] = useState([]);
  const [coachLikes, setCoachLikes] = useState(0);
  const [adminLikes, setAdminLikes] = useState(0);
  const [decrementLoading, setDecrementLoading] = useState(false);

  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  const isLocked = sharedProfile && !hasSubscriptionAccess;
  const [isFavorite, setIsFavorite] = useState(profile?.likes);
  const [isExpanded, setIsExpanded] = useState(true);
  const [openTrialModal, setOpenTrialModal] = useState(false);
  const [openPremiumFeatureModal, setOpenPremiumFeatureModal] = useState(false);
  const [likeLoading, setLikeLoading] = useState(false);
  const [likesCount, setLikesCount] = useState(profile?.likes ?? 0);
  const [adminLikeLoading, setAdminLikeLoading] = useState(false);
  const [badgePulse, setBadgePulse] = useState(false);

  useEffect(() => {
    const profileId = profile?.id;

    const liked =
      r_athlete?.favouriteAthletes?.find((a) => a.userId === profileId)
        ?.liked ?? false;

    setIsFavorite(liked);
  }, [profile?.id, r_athlete?.favouriteAthletes, hasSubscription]);

  // Keep the badge in sync with the backend value so it survives a page refresh
  // (and covers the isolateProfile case, where the profile is not in the store).
  useEffect(() => {
    setLikesCount(profile?.likes ?? 0);
  }, [profile?.likes]);

  useEffect(() => {
    if (!badgePulse) return undefined;
    const timer = setTimeout(() => setBadgePulse(false), 300);
    return () => clearTimeout(timer);
  }, [badgePulse]);

  const loadCoaches = async () => {
    try {
      setLoading(true);
      const data = await getCoachesThatLikeAthlete(apiInstance, profile?.id);
      // Pre-breakdown callers got a bare array from this endpoint; keep
      // supporting that shape in case the route isn't extended everywhere yet.
      const coachesArray = Array.isArray(data)
        ? data
        : Array.isArray(data?.coaches)
          ? data.coaches
          : [];

      const apiCoachLikes = toFiniteNumber(data?.coachLikes);
      const apiAdminLikes = toFiniteNumber(data?.adminLikes);

      const derivedCoachLikes = apiCoachLikes ?? coachesArray.length;
      const totalLikes = toFiniteNumber(profile?.likes) ?? 0;
      const derivedAdminLikes =
        apiAdminLikes ?? Math.max(0, totalLikes - derivedCoachLikes);

      setCoaches(coachesArray);
      setCoachLikes(derivedCoachLikes);
      setAdminLikes(derivedAdminLikes);

      // Only counts the route actually reported are authoritative. If either
      // was derived locally, adminLikes came *from* the badge total and writing
      // it back would be circular. The inequality check keeps this from
      // dispatching a no-op, so opening the dialog can never feed a
      // fetch -> dispatch -> fetch cycle.
      const authoritativeTotal =
        apiCoachLikes != null && apiAdminLikes != null
          ? apiCoachLikes + apiAdminLikes
          : null;

      if (authoritativeTotal != null && authoritativeTotal !== totalLikes) {
        dispatch(
          setProfileLikesForAthlete({
            athleteUserId: profile?.id,
            likes: authoritativeTotal,
          }),
        );
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to load coaches who liked this athlete.",
      );
      setCoaches([]);
      setCoachLikes(0);
      setAdminLikes(0);
    } finally {
      setLoading(false);
    }
  };

  const handleDecrementAdminLike = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();

    const profileId = profile?.id;
    if (!profileId || decrementLoading || adminLikes < 1) return;

    try {
      setDecrementLoading(true);
      const response = await updateAdminLike(apiInstance, false, profileId);
      const total = extractLikeTotal(response);

      if (total == null) {
        await loadCoaches();
        return;
      }

      setAdminLikes(Math.max(0, total - coachLikes));
      dispatch(
        setProfileLikesForAthlete({ athleteUserId: profileId, likes: total }),
      );
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Error removing staff like",
      );
    } finally {
      setDecrementLoading(false);
    }
  };

  const handleOpenDialog = () => {
    if (!hasSubscriptionAccess) {
      setOpenPremiumFeatureModal(true);
      return;
    }
    if (user.role === "athlete" || user.role === "admin") {
      loadCoaches();
      setIsOpen(true);
    }
  };

  const openSubscription = usePremiumUpsellRedirect(user);

  const handleStarClick = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();

    if (!hasSubscriptionAccess) {
      setOpenPremiumFeatureModal(true);
      return;
    }

    const profileId = profile?.id;
    if (!profileId || likeLoading) return;
    const nextValue = !isFavorite;
    setIsFavorite(nextValue);
    dispatch(
      toggleFavouriteLocal({ athleteUserId: profileId, liked: nextValue }),
    );

    try {
      setLikeLoading(true);
      await likeAthlete(apiInstance, nextValue, profileId);
      await onFavoritesRefresh?.();
    } catch (err) {
      setIsFavorite((prev) => !prev);
      dispatch(
        toggleFavouriteLocal({ athleteUserId: profileId, liked: !nextValue }),
      );
      toast.error("Error updating like status", { position: "top-center" });
    } finally {
      setLikeLoading(false);
    }
  };

  const toFiniteNumber = (value) => {
    if (typeof value === "number")
      return Number.isFinite(value) ? value : undefined;
    if (typeof value === "string" && value.trim() !== "") {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : undefined;
    }
    return undefined;
  };

  // The bare response body is ambiguous (a plain-text "1" could be a status
  // token, not a total), so only a strict number is trusted there. A named
  // field is unambiguous, so a numeric string is fine there. The admin-like
  // endpoint responds with { data: "<total>" } (e.g. { "data": "7" }).
  const extractLikeTotal = (data) => {
    if (typeof data === "number" && Number.isFinite(data)) return data;
    return (
      toFiniteNumber(data?.data) ??
      toFiniteNumber(data?.totalLikes) ??
      toFiniteNumber(data?.likes)
    );
  };

  // Admin-only: every click adds one like. Removing likes is handled from the
  // "coaches who liked" dialog, so there is no decrement control here.
  const handleAdminLikeClick = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();

    const profileId = profile?.id;
    if (!isAdmin || !profileId || adminLikeLoading) return;

    setAdminLikeLoading(true);
    setBadgePulse(true);
    setLikesCount((prev) => (prev || 0) + 1);
    dispatch(adjustAdminLikeCountLocal({ athleteUserId: profileId, delta: 1 }));

    try {
      const response = await adminLikeAthlete(apiInstance, true, profileId);
      const total = extractLikeTotal(response);

      if (total !== undefined) {
        setLikesCount(Math.max(0, total));
        dispatch(
          adjustAdminLikeCountLocal({ athleteUserId: profileId, total }),
        );
      }

      toast.success("Like added", { position: "top-center" });
    } catch (err) {
      setLikesCount((prev) => Math.max(0, (prev || 0) - 1));
      dispatch(
        adjustAdminLikeCountLocal({ athleteUserId: profileId, delta: -1 }),
      );
      toast.error("Error adding like", { position: "top-center" });
    } finally {
      setAdminLikeLoading(false);
    }
  };

  const getCircleBackgroundColor = () => {
    return isFavorite ? "bg-yellow-500" : "bg-gray-400";
  };

  const openFreeTrialWidget = () => {
    setOpenTrialModal(true);
  };

  const hasUsedFreeTrial = !!profile?.hasUsedFreeTrial;

  const showSubscribeNow =
    subscriptionChecked && !sharedProfile && !hasSubscriptionAccess;
  const showOfferTier =
    user?.role === "admin" ||
    user?.role === "university" ||
    user?.role === "highschool";

  const showFreeTrial =
    !sharedProfile &&
    !hasSubscriptionAccess &&
    user?.role === "athlete" &&
    !hasUsedFreeTrial;

  const canUseCompare = hasSubscriptionAccess && showCompare;
  const shouldGateCompare = !hasSubscriptionAccess && showCompare;
  const isOwnProfile = String(user?.id) === String(profile?.id);
  const profileLoaded = profile != null && profile?.id != null;
  const canAccessShareProfileCard =
    user?.role === "athlete" && !sharedProfile && isOwnProfile && profileLoaded;
  const canShareProfileCard =
    canAccessShareProfileCard && !!profile?.hasCurrentSubscription;
  const canOfferShareProfileCardUpsell =
    canAccessShareProfileCard && !profile?.hasCurrentSubscription;

  const handleBlockedCompare = () => {
    setOpenPremiumFeatureModal(true);
  };

  const handlePremiumFreeTrial = () => {
    setOpenPremiumFeatureModal(false);
    openFreeTrialWidget();
  };

  const handlePremiumSubscribe = () => {
    setOpenPremiumFeatureModal(false);
    openSubscription();
  };

  return (
    <>
      {/* subscription components  */}
      {showSubscribeNow && (
        <div className="w-full rounded-md border border-red-200 bg-red-50 p-2 mb-4">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            {/* Content */}
            <div className="flex-1">
              <h2 className="text-sm font-normal text-gray-800">
                You're using the unsubscribed experience
              </h2>

              <p className="mt-1 text-sm font-medium  text-red-500">
                Unlock premium features, exclusive content, and full access with
                a subscription.
              </p>
            </div>

            <div className="flex w-full flex-col gap-3 sm:flex-row sm:justify-end lg:w-auto">
              {showFreeTrial && (
                <button
                  className="
              rounded-md border border-red-200 bg-white
              text-sm
              px-4 py-2 font-normal text-gray-800
              transition-all duration-200
              hover:bg-gray-50"
                  onClick={openFreeTrialWidget}
                >
                  Start 7 Day Free Trial
                </button>
              )}
              <button
                className="
              rounded-md bg-red-500 text-sm
              px-4 py-2 font-normal text-white
              transition-all duration-200
              hover:bg-red-600
            "
                onClick={openSubscription}
              >
                Subscribe Now
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="-m-4 flex flex-wrap items-start gap-3 bg-white p-4 pb-6 shadow-md md:m-0 md:gap-4 md:bg-transparent md:p-0 md:pb-0 md:shadow-none">
        <div>
          {isLocked ? (
            <div className="flex items-center gap-2 p-3 bg-gray-50 border border-gray-200 rounded-md">
              <img
                src="/lock-svgrepo-com.svg"
                alt="Locked"
                className="w-5 h-5"
              />
              <p className="text-sm text-gray-700">
                Subscribe to view this profile
              </p>
            </div>
          ) : (
            <div className="flex items-start justify-start gap-4">
              <img
                alt={profile?.firstName}
                src={profile?.profilePicture || "/default-profile-img.png"}
                className="w-24 h-24 rounded-lg object-cover shrink-0"
              />
              {r_athlete?.config?.showProfile && (
                <div>
                  <div className="flex items-start justify-start gap-2">
                    <h2 className="text-lg font-bold text-gray-900">
                      <span className="text-lg font-medium uppercase text-gray-900 flex items-center gap-2 whitespace-nowrap">
                        {profile?.firstName} {profile?.lastName}
                        <CopyUrl athleteProfileId={profile?.id} />
                      </span>
                    </h2>
                  </div>
                  <p className="text-gray-600 text-sm">
                    {profile?.primaryPosition}
                  </p>
                  <p className="text-gray-600 text-sm">
                    #{profile?.positionRank} (Overall Position Rank)
                  </p>
                  <p
                    className={`text-gray-600 text-sm ${!isExpanded ? "hidden md:block" : ""}`}
                  >
                    {profile?.highSchool},{" "}
                    {getStateAbbreviation(profile?.state)} (
                    {profile?.expectedYearOfGraduation})
                  </p>
                  <p
                    className={`text-sm pt-1 whitespace-nowrap ${!isExpanded ? "hidden md:block" : ""}`}
                  >
                    <span className="font-normal text-gray-600 capitalize">
                      Weight:{" "}
                      <span className="font-semibold text-gray-800">
                        {profile?.weight}
                      </span>
                    </span>
                    <span className="font-normal text-gray-600 capitalize pl-4">
                      Height:
                      <span className="font-semibold text-gray-800">
                        {profile?.heightFt}' {profile?.heightIn}"
                      </span>
                    </span>
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        <div
          className={`flex min-w-[280px] flex-1 flex-wrap items-start justify-center gap-3 md:justify-end ${!isExpanded ? "hidden md:flex" : ""}`}
        >
          {showOfferTier && (
            <div className="rounded-xl bg-gray-100 p-4 shadow-sm">
              <div className="text-base font-bold text-gray-900">
                {profile?.offerTier}
              </div>
              <div className="mt-0.5 text-sm text-gray-600">Offer Tier</div>
            </div>
          )}
          {showOfferTier &&
            (profile?.offerAcceptanceStatus !== null ||
              profile?.offerAcceptanceSchool !== null) && (
              <div className="rounded-xl bg-gray-100 p-4 shadow-sm">
                <div className="text-base font-bold text-blue-600">
                  {profile?.offerAcceptanceStatus}
                </div>
                {profile?.offerAcceptanceSchool && (
                  <div className="text-base font-bold text-red-600">
                    {profile?.offerAcceptanceSchool}
                  </div>
                )}
              </div>
            )}

          {r_athlete.config.showLikes && !sharedProfile && (
            <div className="flex flex-col items-center">
              <div className="relative top-1 flex">
                {HighSchoolCoach ? null : user.role === "coach" ||
                  user.role === "university" ? (
                  <button
                    className="mr-2 xl:mr-6 text-[#8E8E93]"
                    onClick={handleStarClick}
                    type="button"
                  >
                    <div
                      className={`flex items-center justify-center
                                            h-[32px] xl:h-[40px]
                                            w-[32px] xl:w-[40px]
                                            rounded-full ${getCircleBackgroundColor()}`}
                    >
                      {likeLoading ? (
                        <LoadingSpinner size="sm" />
                      ) : (
                        <FontAwesomeIcon
                          icon={faThumbsUp}
                          style={{ color: "#fff" }}
                        />
                      )}
                    </div>
                  </button>
                ) : (
                  <div className="relative top-1 flex">
                    <div
                      className={`absolute z-10 font-normal text-sm -top-2 left-7 text-red-500 bg-white border border-gray-300 rounded-full px-1.5 transition-transform duration-300 ${isAdmin ? "cursor-pointer" : ""} ${badgePulse ? "scale-150" : "scale-100"}`}
                      onClick={isAdmin ? handleOpenDialog : undefined}
                    >
                      {likesCount}
                    </div>
                    {isAdmin ? (
                      <button
                        type="button"
                        title="Add a like to this athlete"
                        aria-label="Add a like to this athlete"
                        onClick={handleAdminLikeClick}
                        disabled={adminLikeLoading}
                        className="grow-0 shrink-0 transition-transform duration-150 hover:scale-110 disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        <img
                          alt="Athlete Rating"
                          src="/like.svg"
                          className="w-10 h-10"
                        />
                      </button>
                    ) : (
                      <img
                        alt="Athlete Rating"
                        src="/like.svg"
                        className="w-10 h-10 grow-0 shrink-0"
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {r_athlete.config.showRank && (
            <div className="flex flex-col items-center pt-0 md:pt-0 lg:pt-2">
              {user.role === "athlete" || user.role === "admin" ? (
                <div className="flex flex-col items-center">
                  <CircularProgressBar
                    progress={profile?.positionScore || 0}
                    size={80}
                    strokeWidth={5}
                  />
                  <span className="text-[9px] text-gray-900 -mt-8">
                    Position Score
                  </span>
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  <CircularProgressBar
                    progress={profile?.positionScore || 0}
                    size={80}
                    strokeWidth={5}
                  />
                  <span className="text-[9px] text-gray-900 -mt-8">
                    Position Score
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex w-full justify-center pt-2 pb-1 md:hidden">
          <button
            type="button"
            className="text-blue-600 text-sm flex items-center gap-1 hover:opacity-70"
            onClick={() => setIsExpanded((v) => !v)}
          >
            {isExpanded ? "View less ∧" : "View more ∨"}
          </button>
        </div>
      </div>

      <div className="flex">
        <div className="w-1/2">
          {" "}
          {profile?.profileSummaryVideoLink && (
            <div className="pt-6 lg:pt-1">
              <AthleteMeasurementVideoViewer
                title={`${profile?.firstName} ${profile?.lastName} - Athlete Summary Intro Video`}
                label="Athlete Summary Intro Video"
                videoUrls={[profile.profileSummaryVideoLink]}
              />
            </div>
          )}
        </div>
        <div className="w-1/2">
          {(canUseCompare || shouldGateCompare) && (
            <div className="flex-grow items-center justify-end flex pr-2 pt-10 lg:pt-0 pb-6">
              {canUseCompare ? (
                <AthleteCompareModal
                  athlete={r_athlete}
                  onClose={() => setIsOpen(false)}
                />
              ) : (
                <CompareModalTrigger
                  onOpen={handleBlockedCompare}
                  onCompareCombineHistory={handleBlockedCompare}
                />
              )}
              {canShareProfileCard && (
                <div className="ml-4">
                  <ShareProfileCardButton />
                </div>
              )}
              {canOfferShareProfileCardUpsell && (
                <div className="ml-4">
                  <ShareProfileCardUpsellButton
                    onStartFreeTrial={handlePremiumFreeTrial}
                    onSubscribe={handlePremiumSubscribe}
                    showStartFreeTrial={showFreeTrial}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <>
        <div>
          <div className="flex flex-col justify-left items-end">
            <div>{r_athlete.config.searchEvent && <AthleteSearchEvent />}</div>
          </div>
          <CoachesWhoLikedDialog
            open={isOpen}
            onClose={() => setIsOpen(false)}
            userRole={user.role}
            loading={loading}
            coaches={coaches}
            coachLikes={coachLikes}
            adminLikes={adminLikes}
            onDecrementAdminLike={handleDecrementAdminLike}
            decrementLoading={decrementLoading}
          />
        </div>
      </>
      <FreeTrialModal
        open={openTrialModal}
        onClose={() => setOpenTrialModal(false)}
        user={user}
        onStoreSuccessChain={async () => {
          const result = await StartFreeTrial(apiInstance, user.id);
          if (result.success) {
            toast.success("Free trial started!", { position: "top-right" });
            setOpenTrialModal(false);
            await dispatch(setProfileAction(apiInstance, user.id));
            await dispatch(
              validateSubscriptionAction(apiInstance, user.role, user.id),
            );
            dispatch(bumpMenuRefresh());
          } else {
            toast.error("Failed to start free trial", {
              position: "top-right",
            });
          }
        }}
      />
      <PremiumFeatureModal
        open={openPremiumFeatureModal}
        onClose={() => setOpenPremiumFeatureModal(false)}
        title="Premium Feature"
        description="Compare Athlete is locked. Start a free trial or subscribe now to get full access."
        onStartFreeTrial={handlePremiumFreeTrial}
        onSubscribe={handlePremiumSubscribe}
        showStartFreeTrial={showFreeTrial}
      />
    </>
  );
}
export default AthleteInformation;
